import { useCallback } from 'react';
import { isSome } from 'fp-ts/lib/Option';

import { LayoutType } from 'domain/entity/layout';
import { WidgetType } from 'domain/entity/widget';
import { UpdateLayoutCriteriaBuilder } from 'use-case/layout/update/updateLayoutCriteria';
import { dispatchFormChangesView } from 'adapter/in/common/context/formChanges';
import { dispatchCurrentWidgetConfiguration, KeyAndNamePair } from 'adapter/in/common/context/currentWidgetConfiguration';
import { useNavigation } from 'adapter/in/common/hooks/useNavigation';
import { dispatchDashboardEdit, LAYOUT_STATUS, useDashboardEdit } from 'adapter/in/dashboards/context/EditDashboard';
import { dispatchWidgetMenuView } from 'adapter/in/dashboards/context/EditDashboard/WidgetMenuView';
import { ConfigFieldType } from 'adapter/in/common/form/types';
import { useLayouts } from 'adapter/in/dashboards/context/Layouts';
import { dispatchLayoutsView, useLayoutsView } from 'adapter/in/common/context/Layouts/LayoutsContext';

export const useHeaderDiscardChangesCleanupActions = (): {
    exitEditModeCleanupAction: () => void;
    addWidgetCleanupAction: () => void;
    editLayoutCleanupAction: () => void;
    editLayoutChangeLayoutTypeCleanupAction: (layoutType: LayoutType) => void;
    editLayoutSaveAndProceedAction: () => void;
} => {
    const { setModalDiscardChanges, setShowSidePanelMenu } = dispatchDashboardEdit();
    const { setEditLayoutMode, selectLayoutType, setHasLayoutChanges, setLayoutStatus } = dispatchLayoutsView();
    const { reset: resetCurrentWidgetConfiguration } = dispatchCurrentWidgetConfiguration();
    const { editLayoutMode, selectedLayoutType, layouts } = useLayoutsView();
    const { dashboard, showSidePanelMenu } = useDashboardEdit();
    const { resetWidgetMenu } = dispatchWidgetMenuView();
    const { setHasChange } = dispatchFormChangesView();
    const navigation = useNavigation();
    const { update: updateLayout } = useLayouts();

    const exitEditModeCleanupAction = () => {
        setModalDiscardChanges(false);
        if (editLayoutMode) {
            setEditLayoutMode(false);
            selectLayoutType(LayoutType.DESKTOP);
            setLayoutStatus(LAYOUT_STATUS.FORCE_RESET);
            setHasLayoutChanges(false);
        } else {
            resetWidgetMenu();
            resetCurrentWidgetConfiguration();
            setHasChange(false);
            if (showSidePanelMenu) {
                setShowSidePanelMenu(false);
            }
            if (isSome(dashboard)) {
                navigation.navigationPush(dashboard.value.id);
            }
        }
    };
    const addWidgetCleanupAction = () => {
        setModalDiscardChanges(false);

        if (editLayoutMode) {
            setEditLayoutMode(false);
            selectLayoutType(LayoutType.DESKTOP);
            setLayoutStatus(LAYOUT_STATUS.FORCE_RESET);
        }
        resetWidgetMenu();
        resetCurrentWidgetConfiguration();
        setShowSidePanelMenu(true);
    };
    const editLayoutCleanupAction = () => {
        setModalDiscardChanges(false);
        setHasChange(false);
        if (showSidePanelMenu) {
            setShowSidePanelMenu(false);
        }
        resetWidgetMenu();
        resetCurrentWidgetConfiguration();
        setEditLayoutMode(true);
    };
    const editLayoutChangeLayoutTypeCleanupAction = (layoutType: LayoutType) => {
        setModalDiscardChanges(false);
        setLayoutStatus(LAYOUT_STATUS.FORCE_RESET);
        selectLayoutType(layoutType);
    };

    const editLayoutSaveAndProceedAction = useCallback(() => {
        if (isSome(dashboard)) {
            updateLayout(
                UpdateLayoutCriteriaBuilder.create()
                    .withDashboardId(dashboard.value.id)
                    .withLayout(layouts.get(selectedLayoutType)!)
                    .build()
            );
            setHasLayoutChanges(false);
        }
    }, [layouts, selectedLayoutType, dashboard]);

    return {
        exitEditModeCleanupAction,
        addWidgetCleanupAction,
        editLayoutCleanupAction,
        editLayoutChangeLayoutTypeCleanupAction,
        editLayoutSaveAndProceedAction,
    };
};

type ClickedWidget = {
    id: string;
    type: WidgetType;
    feature: KeyAndNamePair;
    preset: KeyAndNamePair;
    tabPane: ConfigFieldType;
};
export const useWidgetActionsDiscardChangesCleanupActions = (): {
    switchEditWidgetCleanupAction: (clickedWidget: ClickedWidget) => void;
} => {
    const { setModalDiscardChanges } = dispatchDashboardEdit();
    const { setCurrentWidgetConfiguration } = dispatchCurrentWidgetConfiguration();
    const { editWidget, setDisableSaveButtonTo } = dispatchWidgetMenuView();
    const { setHasChange } = dispatchFormChangesView();

    const switchEditWidgetCleanupAction = (clickedWidget: ClickedWidget) => {
        setModalDiscardChanges(false);
        setHasChange(false);

        setCurrentWidgetConfiguration(clickedWidget.type, clickedWidget.feature, clickedWidget.preset, clickedWidget.id);

        editWidget(clickedWidget.tabPane);

        setDisableSaveButtonTo(false);
    };

    return {
        switchEditWidgetCleanupAction,
    };
};

export const useWidgetMenuDiscardChangesCleanupActions = (): { closeWidgetMenuCleanupAction: () => void } => {
    const { setModalDiscardChanges, setShowSidePanelMenu } = dispatchDashboardEdit();
    const { reset: resetCurrentWidgetConfiguration } = dispatchCurrentWidgetConfiguration();
    const { setHasChange } = dispatchFormChangesView();
    const { resetWidgetMenu } = dispatchWidgetMenuView();

    const closeWidgetMenuCleanupAction = () => {
        setShowSidePanelMenu(false);
        setModalDiscardChanges(false);
        // TODO: This reset action should be called when the side panel animation has ended and the side panel is closed
        resetWidgetMenu();
        resetCurrentWidgetConfiguration();
        setHasChange(false);
    };

    return {
        closeWidgetMenuCleanupAction,
    };
};
